import React from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './SectionHeader.css';

/**
 * Reusable Section Header Component
 * Provides consistent styling, layout, and animations for main sections
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.title - Section title
 * @param {string} props.subtitle - Optional subtitle
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Toggle handler function
 * @param {boolean} props.showToggle - Whether to show toggle button (default: true)
 * @param {string} props.iconAnimation - Icon animation type: 'rotate', 'scale', 'float', 'none' (default: 'none'). Use 'none' for restraint; motion only for transitions/key CTAs.
 */
function SectionHeader({ 
  icon, 
  title, 
  subtitle, 
  isExpanded, 
  onToggle, 
  showToggle = true,
  iconAnimation = 'none'
}) {
  // Icon animation variants (use sparingly; default 'none' for visual restraint)
  const iconAnimations = {
    rotate: {
      rotate: [0, -15, 15, 0],
    },
    scale: {
      scale: [1, 1.1, 1],
    },
    float: {
      y: [0, -8, 0],
    },
    pulse: {},
    none: {}
  };

  const iconAnimationProps = iconAnimations[iconAnimation] || iconAnimations.none;
  const hasDecoration = iconAnimation !== 'none' && Object.keys(iconAnimationProps).length > 0;

  return (
    <div className={`section-header ${!isExpanded ? 'collapsed' : ''}`}>
      <div className="header-with-toggle">
        <div className="section-header-content">
          <motion.div
            className="section-icon-wrapper"
            animate={hasDecoration ? iconAnimationProps : {}}
            transition={hasDecoration ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : {}}
          >
            {icon}
          </motion.div>
          <div>
            <h2>{title}</h2>
            {subtitle && (
              <p className="section-subtitle-creative">{subtitle}</p>
            )}
          </div>
        </div>
        {showToggle && onToggle && (
          <motion.button
            className="section-toggle-btn"
            onClick={onToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label={isExpanded ? "Collapse section" : "Expand section"}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </motion.button>
        )}
      </div>
    </div>
  );
}

export default SectionHeader;








